import * as React from 'react';
import YouTube from 'react-youtube';
import FocusTrap from 'focus-trap-react';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Tooltip from '@material-ui/core/Tooltip';
import { OutboundLink } from 'react-ga';

import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { LogoLockup } from '../LogoLockup/LogoLockup';
import { MiniPlayer } from '../MiniPlayer/MiniPlayer';
import { WaveBars } from '../Wave/WaveBars';
import { Social } from '../Social/Social';
import { AboutTabPanel as TabPanel } from './AboutTabPanel';
import { FILM_YOUTUBE_ID, PATHS, TRACKING_LABELS } from '../../constants';
import { LOGO } from '../../svg-assets';
import { TRAINING_DATA_SHAPES } from './TrainingDataAudioShapes';

import './About.scss';

interface Props {
  handleComponentRendered?: Function;
  hidden?: boolean;
  showHeader: boolean;
  showPlayers: boolean;
}

interface State {
  activePlayerKey?: string;
  activeTabIndex: number;
  isTooltipOpen: boolean;
  trainingDataTabs: Array<{
    hours: number;
    audioSource: string;
    audioShape?: number[];
  }>;
}

const TRAINING_DATA_TABS = [
  {
    audioShape: TRAINING_DATA_SHAPES.SHAPE_1_HOUR,
    audioSource: `${PATHS.CURATED_AUDIO}/about/2_training--1-hour.mp3`,
    hours: 1,
  },
  {
    audioShape: TRAINING_DATA_SHAPES.SHAPE_3_HOURS,
    audioSource: `${PATHS.CURATED_AUDIO}/about/2_training--3-hours.mp3`,
    hours: 3,
  },
  {
    audioShape: TRAINING_DATA_SHAPES.SHAPE_10_HOURS,
    audioSource: `${PATHS.CURATED_AUDIO}/about/2_training--10-hours.mp3`,
    hours: 10,
  },
];

export default class About extends React.Component<Props, State> {
  youTubePlayerEl?: YT.Player;

  constructor(props) {
    super(props);
    this.state = {
      activeTabIndex: 0,
      isTooltipOpen: false,
      trainingDataTabs: [],
    };
  }

  static defaultProps = {
    showHeader: true,
    showPlayers: true,
  };

  componentDidMount() {
    if (this.props.handleComponentRendered) {
      this.props.handleComponentRendered();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.youTubePlayerEl &&
      prevProps.showPlayers &&
      !this.props.showPlayers
    ) {
      this.youTubePlayerEl.pauseVideo();
    }
  }

  handleChangeTab = (_, index: number) => {
    this.setState({ activeTabIndex: index });
  };

  handleVideoReady = (event: YT.OnStateChangeEvent) => {
    this.youTubePlayerEl = event.target;
  };

  setActivePlayerKey = (key: string) => {
    this.setState({ activePlayerKey: key });
  };

  handleTooltipClose = () => {
    this.setState({ isTooltipOpen: false });
  };

  handleTooltipOpen = () => {
    this.setState({ isTooltipOpen: true });
  };

  render() {
    const { hidden } = this.props;
    return (
      <article hidden={hidden} className="about">
        {this.props.showHeader && (
          <header>
            <h1>{LOGO}</h1>
          </header>
        )}
        <FocusTrap
          active={!hidden}
          focusTrapOptions={{
            clickOutsideDeactivates: true,
            initialFocus: '#about-main',
          }}
        >
          <main id="about-main">
            <section>
              <h2>Why is Tone Transfer unique?</h2>
              <div className="about__grid-row">
                <div>
                  <p>
                    Tone Transfer uses machine learning models to transform
                    sounds into musical instruments used in a variety of styles,
                    from Baroque to jazz. Rerender the chirp of a bird into a
                    flute. Turn a cello piece into a saxophone solo. Tone
                    Transfer makes these transformations possible through a new
                    ML technique called Differentiable Digital Signal
                    Processing, or DDSP. Since the early 70s, synthesizers have
                    tried to recreate musical instruments but often end up
                    sounding unrealistic. Modern computers have made it easy to
                    sample high quality recordings of every note of an
                    instrument, but they lead to millions of recordings just for
                    a single instrument. Tone Transfer uses ML to directly learn
                    the tone of a musical instrument. The technology can extract
                    relationships between pitch, tone, and volume from very
                    small amounts of data, and form a high quality and
                    expressive audio synthesis model of the output instrument.
                    The model is flexible and can be controlled by inputs such
                    as pitch and volume.
                  </p>
                </div>
                <div className="about__film">
                  <div className="about__film-container">
                    <YouTube
                      opts={{
                        height: '100%',
                        width: '100%',
                      }}
                      onReady={this.handleVideoReady}
                      videoId={FILM_YOUTUBE_ID}
                    />
                  </div>
                </div>
              </div>
              <div className="about__grid-row about__intro-features">
                <div className="about__intro-feature">
                  <div
                    className="about__intro-feature__image"
                    style={{
                      backgroundImage: `url(${PATHS.IMAGES}/about/birds.png)`,
                    }}
                  ></div>
                  <div>
                    <h3>Create impossible music</h3>
                    <p>
                      You can perform impossible melodies on the output
                      instrument. Get a flute to chirp just like a bird. Get a
                      saxophone to play notes that don’t actually exist on the
                      buttons of the instrument.
                    </p>
                  </div>
                </div>
                <div className="about__intro-feature">
                  <div
                    className="about__intro-feature__image"
                    style={{
                      backgroundImage: `url(${PATHS.IMAGES}/about/spectrograph.png)`,
                    }}
                  ></div>
                  <div>
                    <h3>Harder, Better, Stronger</h3>
                    <p>
                      Tone Transfer models only require about 10 minutes of data
                      from the output instrument instead of thousands of hours.
                      Once trained, the model can render other sounds into the
                      output instrument within seconds, instead of minutes.
                    </p>
                  </div>
                </div>
                <div className="about__intro-feature">
                  <div
                    className="about__intro-feature__image"
                    style={{
                      backgroundImage: `url(${PATHS.IMAGES}/about/performance.png)`,
                    }}
                  ></div>
                  <div>
                    <h3>Data-driven performance details</h3>
                    <p>
                      Tone Transfer models can resynthesize performance details
                      such as bowing noises on a violin, the breath of a
                      flutist, and button presses on a saxophone.
                    </p>
                  </div>
                </div>
              </div>
            </section>
            <section className="about__process">
              <h2>How we made our Tone Transfer ML models</h2>
              <section className="about__grid-row">
                <div>
                  <h3>Capturing the audio</h3>
                  <p>
                    We provided the ML model with a 10-minute recording of a
                    sound, such as a musical instrument. Any sound will work,
                    but it must be monophonic, like this violin (only one note
                    playing at any moment)
                  </p>
                </div>
                <div>
                  <figure className="about__illustration about__illustration--extraction">
                    <div
                      className="about__illustration__background"
                      style={{
                        backgroundImage: `url(${PATHS.IMAGES}/about/watercolor.png)`,
                      }}
                    ></div>
                    <div className="about__illustration__instrument">
                      <img
                        src={PATHS.IMAGES + '/about/violin.png'}
                        alt=""
                        role="presentation"
                      />
                    </div>
                    <div className="about__illustration__split-column about__illustration__training-audio">
                      <img
                        src={
                          PATHS.IMAGES +
                          '/about/illustration-extraction__training-waveform.svg'
                        }
                        alt=""
                        role="presentation"
                      />
                      <MiniPlayer
                        activePlayerKey={this.state.activePlayerKey}
                        label="Training audio"
                        isFocusable={!hidden}
                        playerKey="1_original"
                        setActivePlayerKey={this.setActivePlayerKey}
                        src={PATHS.CURATED_AUDIO + '/about/1_original.mp3'}
                      />
                    </div>
                    <div className="about__illustration__centered-column about__illustration__column--split-arrow">
                      <img
                        src={
                          PATHS.IMAGES + '/about/illustration--arrow-split.svg'
                        }
                        alt=""
                        role="presentation"
                      />
                    </div>
                    <div className="about__illustration__split-column about__illustration--extraction__pitch-and-loudness">
                      <div className="about__illustration__split-column">
                        <img
                          src={
                            PATHS.IMAGES +
                            '/about/illustration--extraction__pitch-waveform.svg'
                          }
                          alt=""
                          role="presentation"
                        />
                        Pitch
                      </div>
                      <div className="about__illustration__split-column">
                        <img
                          src={
                            PATHS.IMAGES +
                            '/about/illustration--extraction__loudness-waveform.svg'
                          }
                          alt=""
                          role="presentation"
                        />
                        Loudness
                      </div>
                    </div>
                  </figure>
                </div>
              </section>
              <section className="about__grid-row">
                <div>
                  <h3>Training the machine learning model</h3>
                  <p>
                    At the start of training, the model does a clumsy job at
                    recreating the tone of the instrument. It sounds like a
                    basic synthesizer from the 70s. However, after a couple of
                    hours into the training process, the model quickly learns to
                    produce audio that sounds like the output violin.
                  </p>
                </div>
                <div className="tabs">
                  <div className="tabs__tabset">
                    <Tabs
                      aria-label="Training data clips"
                      onChange={this.handleChangeTab}
                      value={this.state.activeTabIndex}
                    >
                      {TRAINING_DATA_TABS.map((tab, index) => (
                        <Tab
                          disabled={hidden}
                          key={`training-data-tab__${index}`}
                          id={`training-data-tab__${index}`}
                          aria-controls={`training-data__tab-content__${index}`}
                          label={
                            <div className="tabs_tab">
                              <strong className="tabs__tab__strong">
                                {tab.hours}
                              </strong>
                              <span className="tabs__tab__label">
                                {tab.hours === 1 ? 'hour' : 'hours'}
                                {` `} of training
                              </span>
                            </div>
                          }
                        ></Tab>
                      ))}
                    </Tabs>
                  </div>
                  <div className="tabs__content-container">
                    {TRAINING_DATA_TABS.map((tab, index) => (
                      <TabPanel
                        key={`training-data__tab-content__${index}`}
                        activeIndex={this.state.activeTabIndex}
                        id={`training-data__tab-content__${index}`}
                        index={index}
                        labeledBy={`training-data-tab__${index}`}
                      >
                        {this.props.showPlayers && (
                          <div className="tabs__content__player">
                            <MiniPlayer
                              activePlayerKey={this.state.activePlayerKey}
                              isFocusable={!hidden}
                              playerKey={`${tab.hours}-hours-training`}
                              setActivePlayerKey={this.setActivePlayerKey}
                              src={tab.audioSource}
                            />
                            <div className="tabs__content__waveform">
                              <WaveBars
                                waveformCoordinates={tab.audioShape.map(
                                  (y, index) => [
                                    (100 / (tab.audioShape.length - 1)) * index,
                                    y + 1,
                                  ]
                                )}
                              />
                            </div>
                          </div>
                        )}
                      </TabPanel>
                    ))}
                  </div>
                </div>
              </section>
              <section className="about__grid-row">
                <div>
                  <h3>Fully trained model with digital signal processors</h3>
                  <p>
                    After training is complete, the ML model has learned how to
                    control a collection of digital signal processors that each
                    make up a portion of the final sound. These include the
                    pitched components, non-pitched components (like bowing
                    noises), and reverberations of the room the original violin
                    data was recorded in.
                  </p>
                </div>
                <div>
                  <figure className="about__illustration about__illustration--processing">
                    <div className="about__illustration__split-column about__illustration--processing__reverb-and-noise">
                      <div>
                        <img
                          src={
                            PATHS.IMAGES +
                            '/about/illustration--processing__additive-waveform.svg'
                          }
                          alt=""
                          role="presentation"
                        />
                        <MiniPlayer
                          activePlayerKey={this.state.activePlayerKey}
                          isFocusable={!hidden}
                          label="Additive synth"
                          playerKey="3_additive"
                          setActivePlayerKey={this.setActivePlayerKey}
                          src={PATHS.CURATED_AUDIO + '/about/3_additive.mp3'}
                        />
                      </div>
                      <div>
                        <img
                          src={
                            PATHS.IMAGES +
                            '/about/illustration--processing__noise-waveform.svg'
                          }
                          alt=""
                          role="presentation"
                        />
                        <MiniPlayer
                          activePlayerKey={this.state.activePlayerKey}
                          isFocusable={!hidden}
                          label="Filtered noise synth"
                          playerKey="3_noise"
                          setActivePlayerKey={this.setActivePlayerKey}
                          src={PATHS.CURATED_AUDIO + '/about/3_noise.mp3'}
                        />
                      </div>
                    </div>
                    <div className="about__illustration__centered-column about__illustration__column--join-arrow">
                      <img
                        src={
                          PATHS.IMAGES + '/about/illustration--arrow-join.svg'
                        }
                        alt=""
                        role="presentation"
                      />
                    </div>
                    <div className="about__illustration__split-column about__illustration--processing__additive-reverb">
                      <img
                        src={
                          PATHS.IMAGES +
                          '/about/illustration--processing__reverb.svg'
                        }
                        alt=""
                        role="presentation"
                      />
                      <MiniPlayer
                        activePlayerKey={this.state.activePlayerKey}
                        isFocusable={!hidden}
                        label="Reverb module"
                        playerKey="3_additive--reverb"
                        setActivePlayerKey={this.setActivePlayerKey}
                        src={
                          PATHS.CURATED_AUDIO + '/about/3_additive--reverb.mp3'
                        }
                      />
                    </div>
                  </figure>
                </div>
              </section>
              <section className="about__grid-row">
                <div>
                  <h3>Synthesize audio using pitch and volume</h3>
                  <p>
                    The ML model is now able to synthesize audio that sounds
                    like the original violin given any new combination of pitch
                    and volume. This includes pitches that were not heard in the
                    original training data.
                  </p>
                </div>
                <div>
                  <figure className="about__illustration about__illustration--resynthesize">
                    <div
                      className="about__illustration__background"
                      style={{
                        backgroundImage: `url(${PATHS.IMAGES}/about/watercolor.png)`,
                      }}
                    ></div>
                    <div className="about__illustration__split-column">
                      <img
                        src={
                          PATHS.IMAGES +
                          '/about/illustration--resynthesize__original-waveform.svg'
                        }
                        alt=""
                        role="presentation"
                      />
                      <MiniPlayer
                        activePlayerKey={this.state.activePlayerKey}
                        isFocusable={!hidden}
                        label="Original training audio"
                        playerKey="1_original"
                        setActivePlayerKey={this.setActivePlayerKey}
                        src={PATHS.CURATED_AUDIO + '/about/1_original.mp3'}
                      />
                    </div>
                    <div className="about__illustration__instrument">
                      <img
                        src={PATHS.IMAGES + '/about/violin.png'}
                        alt=""
                        role="presentation"
                      />
                    </div>
                    <div className="about__illustration__split-column">
                      <img
                        src={
                          PATHS.IMAGES +
                          '/about/illustration--resynthesize__new-waveform.svg'
                        }
                        alt=""
                        role="presentation"
                      />
                      <MiniPlayer
                        activePlayerKey={this.state.activePlayerKey}
                        isFocusable={!hidden}
                        label="Resynthesized audio"
                        playerKey="4_resynthesized"
                        setActivePlayerKey={this.setActivePlayerKey}
                        src={PATHS.CURATED_AUDIO + '/about/4_resynthesized.mp3'}
                      />
                    </div>
                  </figure>
                </div>
              </section>
            </section>
            <section>
              <h2>Learn more about our technology and mission</h2>
              <div className="about__grid-row">
                <div className="about__grid-stacked-column">
                  <div>
                    <h3>Differentiable Digital Signal Processing (DDSP)</h3>
                    <p>
                      DDSP combines the interpretable structure of classical DSP
                      elements (such as filters, oscillators, and reverberation)
                      with the expressivity of modern ML. Learn more about the
                      technical details through the links below.
                    </p>
                    <div>
                      <OutboundLink
                        eventLabel={TRACKING_LABELS.LINK_CLICK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="about__button"
                        to="https://g.co/magenta/ddsp"
                        tabIndex={hidden ? -1 : 0}
                      >
                        Read the blog post
                      </OutboundLink>
                      <OutboundLink
                        eventLabel={TRACKING_LABELS.LINK_CLICK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="about__button"
                        to="https://github.com/magenta/ddsp"
                        tabIndex={hidden ? -1 : 0}
                      >
                        Get it on Github
                      </OutboundLink>
                    </div>
                  </div>
                  <div>
                    <h3>On-device ML vs. cloud ML</h3>
                    <p>
                      This experiment is built with a smaller version of{' '}
                      <OutboundLink
                        eventLabel={TRACKING_LABELS.LINK_CLICK}
                        target="_blank"
                        rel="noopener noreferrer"
                        to="https://ai.googleblog.com/2019/11/spice-self-supervised-pitch-estimation.html"
                        tabIndex={hidden ? -1 : 0}
                      >
                        SPICE
                      </OutboundLink>
                      , which detects pitch, and{' '}
                      <OutboundLink
                        eventLabel={TRACKING_LABELS.LINK_CLICK}
                        target="_blank"
                        rel="noopener noreferrer"
                        to="https://github.com/magenta/ddsp"
                        tabIndex={hidden ? -1 : 0}
                      >
                        DDSP
                      </OutboundLink>
                      , which generates sounds directly on your device. To run a
                      larger and higher-quality model, check out our Python
                      version in the Colab demo below.
                    </p>
                    <div>
                      <OutboundLink
                        eventLabel={TRACKING_LABELS.LINK_CLICK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="about__button"
                        to={PATHS.COLAB}
                        tabIndex={hidden ? -1 : 0}
                      >
                        Try out the Colab demo
                      </OutboundLink>
                    </div>
                  </div>
                </div>
                <div className="about__grid-stacked-column">
                  <div>
                    <h3>Open source at our heart</h3>
                    <p>
                      Tone Transfer was built on the DDSP library created by
                      Magenta, an open source research project exploring the role
                      of ML as a tool in the creative process. All our models,
                      experiments, and more can be found on our blog and github.
                      We encourage you to try out some of our pretrained models
                      and contribute to the open source community!
                    </p>
                  </div>
                  <div>
                    <h3>Tone Transfer in real time</h3>
                    <p>
                      This experiment converts an entire audio segment all at 
                      once for an interactive web experience that is good for 
                      composition. To experience real-time tone transfer as a 
                      playable instrument in your digital audio workstation, try 
                      the VST plugin.
                    </p>
                    <div>
                      <OutboundLink
                        eventLabel={TRACKING_LABELS.LINK_CLICK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="about__button"
                        to={PATHS.PLUGIN}
                        tabIndex={hidden ? -1 : 0}
                      >
                        Try the plugin
                      </OutboundLink>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <section>
              <h2>Who built this experiment?</h2>
              <div className="about__grid-row">
                <div>
                  <h3>Our team</h3>
                  <p>
                    This experiment was created by the{' '}
                    <OutboundLink
                      eventLabel={TRACKING_LABELS.LINK_CLICK}
                      target="_blank"
                      rel="noopener noreferrer"
                      to="https://g.co/magenta/"
                      tabIndex={hidden ? -1 : 0}
                    >
                      Magenta
                    </OutboundLink>{' '}
                    and AIUX team within{' '}
                    <OutboundLink
                      eventLabel={TRACKING_LABELS.LINK_CLICK}
                      target="_blank"
                      rel="noopener noreferrer"
                      to="https://research.google/"
                      tabIndex={hidden ? -1 : 0}
                    >
                      Google Research
                    </OutboundLink>
                    . We’re a cross-functional group of musicians, researchers,
                    engineers, and designers who collaborate together to make
                    music even more playful and experimental than ever with ML.
                  </p>
                  <blockquote>
                    <span>
                      “AI isn’t just creating new kinds of art; it’s creating
                      new kinds of artists. We want to extend, not replace, the
                      creative process.”
                    </span>
                    - Doug Eck, Research Lead for Magenta
                  </blockquote>
                </div>
                <div>
                  <h3>Our contributors</h3>
                  <p>
                    This experiment wouldn’t have been possible without the many
                    contributions of musicians who gave us the training data. We
                    intend to expand our training data over time to represent
                    many musical cultures from across the world.
                  </p>
                  <div className="about__grid-row about__contributors">
                    <div>
                      <h4>Inputs</h4>
                      <ul>
                        <li>Acapella: Marcus Lee</li>
                        <li>Carnatic Singing: Divya Chandra</li>
                        <li>Cello: Justin Zhao</li>
                        <li>
                          <ClickAwayListener
                            onClickAway={this.handleTooltipClose}
                          >
                            <Tooltip
                              arrow
                              open={this.state.isTooltipOpen}
                              placement="top"
                              title={
                                <div className="about__contributors__citation">
                                  <h4 className="about__contributors__citation__heading">
                                    Citations
                                  </h4>
                                  <ol aria-live="polite">
                                    <li>
                                      Elie JE and Theunissen FE. The vocal
                                      repertoire of the domesticated zebra
                                      finch: a data driven approach to decipher
                                      the information-bearing acoustic features
                                      of communication signals. Animal
                                      Cognition. 2016. 19(2) 285-315 DOI
                                      10.1007/s10071-015-0933-6 [OA
                                      eScholarship]
                                    </li>
                                    <li>
                                      Elie JE and Theunissen FE. Zebra Finches
                                      identify individuals using vocal
                                      signatures unique to each call type.
                                      Nature communication. 2018. 9:4026. doi:
                                      10.1038/s41467-018-06394-9 [OA]
                                    </li>
                                  </ol>
                                </div>
                              }
                            >
                              <div>
                                Zebra Finches:{' '}
                                <span
                                  className="about__contributors__citation__trigger"
                                  aria-label="Citation"
                                  onClick={this.handleTooltipOpen}
                                  role="button"
                                >
                                  Elie JE and Theunissen FE
                                </span>
                              </div>
                            </Tooltip>
                          </ClickAwayListener>
                        </li>
                        <li>
                          Pots, pans and synthesizers: Lamtharn (Hanoi)
                          Hantrakul
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4>Training Data</h4>
                      <ul>
                        <li>Violin: Musopen (Creative Commons)</li>
                        <li>Flute: Musopen (Creative Commons)</li>
                        <li>Trumpet: University of Rochester</li>
                        <li>Saxophone: Kevin Malta</li>
                      </ul>
                    </div>
                  </div>
                  <h3>More like this</h3>
                  <p>
                    Check out more experiments like this one on{' '}
                    <OutboundLink
                      eventLabel={TRACKING_LABELS.LINK_CLICK}
                      target="_blank"
                      rel="noopener noreferrer"
                      to="https://g.co/magenta/demos/"
                      tabIndex={hidden ? -1 : 0}
                    >
                      Magenta demos
                    </OutboundLink>{' '}
                    and{' '}
                    <OutboundLink
                      eventLabel={TRACKING_LABELS.LINK_CLICK}
                      target="_blank"
                      rel="noopener noreferrer"
                      to="https://experiments.withgoogle.com/collection/ai"
                      tabIndex={hidden ? -1 : 0}
                    >
                      AI experiments
                    </OutboundLink>
                    .
                  </p>
                  <div className="about__grid-row about__more">
                    <OutboundLink
                      eventLabel={TRACKING_LABELS.LINK_CLICK}
                      target="_blank"
                      rel="noopener noreferrer"
                      to="https://drumbot.glitch.me/"
                      tabIndex={hidden ? -1 : 0}
                    >
                      <figure>
                        <img
                          src={PATHS.IMAGES + '/about/drumbot.png'}
                          alt=""
                          role="presentation"
                        />
                        <figcaption>Drumbot</figcaption>
                      </figure>
                    </OutboundLink>
                    <OutboundLink
                      eventLabel={TRACKING_LABELS.LINK_CLICK}
                      target="_blank"
                      rel="noopener noreferrer"
                      to="https://magenta.github.io/listen-to-transformer/"
                      tabIndex={hidden ? -1 : 0}
                    >
                      <figure>
                        <img
                          src={
                            PATHS.IMAGES + '/about/listen-to-transformer.png'
                          }
                          alt=""
                          role="presentation"
                        />
                        <figcaption>Listen to Transformer</figcaption>
                      </figure>
                    </OutboundLink>
                  </div>
                </div>
              </div>
            </section>
            <footer className="about__footer">
              {!hidden && <LogoLockup />}
              <div className="about__footer__legal">
                <OutboundLink
                  eventLabel={TRACKING_LABELS.LINK_CLICK}
                  target="_blank"
                  rel="noopener noreferrer"
                  to="https://policies.google.com/"
                  tabIndex={hidden ? -1 : 0}
                >
                  Privacy &amp; Terms
                </OutboundLink>
              </div>
              {!hidden && <Social />}
            </footer>
          </main>
        </FocusTrap>
      </article>
    );
  }
}
